import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

// buildKpiDateRange uses strict format parsing, which requires this plugin.
// Register it here so the behavior is self-contained rather than relying on
// another module having happened to extend dayjs first.
dayjs.extend(customParseFormat);

export const KPI_ISO_DATE_FORMAT = "YYYY-MM-DD";

/** Order in which we probe a nested payload object for the daily row array. */
export const DAILY_NESTED_ARRAY_KEYS = [
  "daily",
  "daily_performance",
  "series",
  "points",
  "items",
  "data",
] as const;

export const coerceKpiNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

/**
 * Builds an inclusive list of 'YYYY-MM-DD' dates between start and end using
 * strict parsing. Returns [] when either bound is empty/invalid or reversed.
 */
export const buildKpiDateRange = (start: string, end: string): string[] => {
  if (!start || !end) return [];
  const startDay = dayjs(start, KPI_ISO_DATE_FORMAT, true);
  const endDay = dayjs(end, KPI_ISO_DATE_FORMAT, true);
  if (!startDay.isValid() || !endDay.isValid()) return [];
  if (endDay.isBefore(startDay, "day")) return [];

  const dates: string[] = [];
  let cursor = startDay;
  while (!cursor.isAfter(endDay, "day")) {
    dates.push(cursor.format(KPI_ISO_DATE_FORMAT));
    cursor = cursor.add(1, "day");
  }
  return dates;
};

/**
 * Unwraps a daily-performance payload into a flat row array. Accepts either a
 * bare array or an object whose rows live under one of DAILY_NESTED_ARRAY_KEYS
 * (probed in precedence order). Non-object entries are dropped.
 */
export const extractDailyRows = <T>(payload: unknown): T[] => {
  if (!payload) return [];

  const isRow = (entry: unknown): entry is T =>
    typeof entry === "object" && entry !== null;

  if (Array.isArray(payload)) {
    return payload.filter(isRow);
  }

  if (typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  for (const key of DAILY_NESTED_ARRAY_KEYS) {
    const nested = record[key];
    if (Array.isArray(nested)) {
      return nested.filter(isRow);
    }
  }

  return [];
};
